import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Users, UserPlus, Shield, ShieldCheck, ShoppingCart,
    X, AlertCircle, Trash2, ToggleLeft, ToggleRight, Edit2
} from 'lucide-react';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario/users/`;

const ROLES = ['Cajero', 'Supervisor'];

const ROL_META = {
    Administrador: { color: 'bg-brand-500/20 text-brand-800 border-brand-500/30', icon: <ShieldCheck size={12} /> },
    Supervisor:    { color: 'bg-alerta-500/20  text-alerta-300  border-alerta-500/30',   icon: <Shield size={12} /> },
    Cajero:        { color: 'bg-exito-500/20 text-exito-300 border-exito-500/30', icon: <ShoppingCart size={12} /> },
    'Sin rol':     { color: 'bg-gray-500/20   text-gray-400   border-gray-500/30',    icon: null },
};

const EMPTY_FORM = { username: '', email: '', password: '', first_name: '', last_name: '', is_active: true, rol: 'Cajero' };

export default function UsuariosManager() {
    const [usuarios, setUsuarios] = useState([]);
    const [planInfo, setPlanInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState(null); // null | 'create' | 'edit'
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const me = JSON.parse(sessionStorage.getItem('user') || '{}');

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [ru, rp] = await Promise.all([
                axios.get(API),
                axios.get(`${API}plan_info/`),
            ]);
            setUsuarios(ru.data);
            setPlanInfo(rp.data);
        } catch {
            setError('Error al cargar usuarios.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const openCreate = () => {
        setFormData(EMPTY_FORM);
        setError(null);
        setModal('create');
    };

    const openEdit = (u) => {
        setFormData({ ...u, password: '', rol: u.rol_display === 'Administrador' ? 'Administrador' : (u.rol_display || 'Cajero') });
        setError(null);
        setModal('edit');
    };

    const closeModal = () => { setModal(null); setError(null); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload = { ...formData };
            if (!payload.password) delete payload.password;
            if (modal === 'edit') {
                await axios.put(`${API}${formData.id}/`, payload);
            } else {
                await axios.post(API, payload);
            }
            closeModal();
            fetchAll();
        } catch (e) {
            const d = e.response?.data;
            const msg = d?.error || d?.message
                || d?.username?.[0]
                || d?.email?.[0]
                || (typeof d === 'string' ? d : null)
                || 'Error al guardar.';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (u) => {
        try {
            await axios.put(`${API}${u.id}/`, { ...u, password: undefined, is_active: !u.is_active });
            fetchAll();
        } catch {
            alert('No se pudo cambiar el estado.');
        }
    };

    const handleDelete = async (u) => {
        if (!window.confirm(`¿Eliminar al usuario "${u.username}"? Esta acción no se puede deshacer.`)) return;
        try {
            await axios.delete(`${API}${u.id}/`);
            fetchAll();
        } catch (e) {
            alert(e.response?.data?.error || 'No se pudo eliminar.');
        }
    };

    const isMe = (u) => u.email === me.email;
    const isAdmin = (u) => u.is_superuser;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-slate-900/60 backdrop-blur-sm p-6 rounded-2xl border border-slate-200 dark:border-white/10 gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-brand-500/20 p-3 rounded-xl border border-brand-500/30">
                        <Users size={24} className="text-brand-800" />
                    </div>
                    <div>
                        <h2 className="titulo-modal">Usuarios y Personal</h2>
                        <p className="text-sm text-slate-500 dark:text-gray-400">Gestioná quién accede a tu sistema</p>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {/* Contador de plan */}
                    {planInfo && (
                        <div className="text-right">
                            <p className="text-xs text-slate-400 dark:text-gray-500 uppercase tracking-wider">Plan {planInfo.plan_name}</p>
                            <p className="text-sm font-bold text-slate-900 dark:text-white">
                                {planInfo.current_users}
                                {planInfo.max_users ? ` / ${planInfo.max_users}` : ''}
                                <span className="text-slate-400 dark:text-gray-500 font-normal"> usuarios</span>
                            </p>
                            {planInfo.max_users && (
                                <div className="w-32 h-1.5 bg-slate-200 dark:bg-white/10 rounded-full mt-1 overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all ${planInfo.slots_left === 0 ? 'bg-error-500' : 'bg-brand-500'}`}
                                        style={{ width: `${Math.min((planInfo.current_users / planInfo.max_users) * 100, 100)}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        onClick={openCreate}
                        disabled={planInfo && !planInfo.can_create}
                        title={planInfo && !planInfo.can_create ? 'Límite de usuarios alcanzado según tu plan' : ''}
                        className="bg-brand-600 hover:bg-brand-500 disabled:opacity-40 disabled:cursor-not-allowed text-negro-950 px-5 py-2.5 rounded-xl shadow-lg shadow-brand-500/30 font-semibold flex items-center gap-2 transition-all"
                    >
                        <UserPlus size={18} />
                        Nuevo Usuario
                    </button>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="tabla-cabecera">
                                <th className="px-6 py-4 font-medium">Usuario</th>
                                <th className="px-6 py-4 font-medium">Nombre</th>
                                <th className="px-6 py-4 font-medium">Email</th>
                                <th className="px-6 py-4 font-medium text-center">Rol</th>
                                <th className="px-6 py-4 font-medium text-center">Estado</th>
                                <th className="px-6 py-4 font-medium text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="tabla-filas">
                            {loading && (
                                <tr><td colSpan="6" className="px-6 py-10 text-center text-slate-400 dark:text-gray-500">Cargando...</td></tr>
                            )}
                            {!loading && usuarios.map(u => {
                                const meta = ROL_META[u.rol_display] || ROL_META['Sin rol'];
                                return (
                                    <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-brand-500/20 flex items-center justify-center text-brand-800 font-bold border border-brand-500/30 shrink-0">
                                                    {u.username[0].toUpperCase()}
                                                </div>
                                                <span className="text-slate-900 dark:text-white font-medium">
                                                    {u.username}
                                                    {isMe(u) && <span className="ml-2 text-[10px] text-brand-800 font-bold">(vos)</span>}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 dark:text-gray-300">
                                            {[u.first_name, u.last_name].filter(Boolean).join(' ') || <span className="text-slate-400 dark:text-gray-600 italic">—</span>}
                                        </td>
                                        <td className="px-6 py-4 text-slate-500 dark:text-gray-400">{u.email}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${meta.color}`}>
                                                {meta.icon}
                                                {u.rol_display}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                onClick={() => !isMe(u) && handleToggleActive(u)}
                                                disabled={isMe(u)}
                                                title={isMe(u) ? 'No podés desactivarte a vos mismo' : (u.is_active ? 'Desactivar' : 'Activar')}
                                                className="disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                {u.is_active
                                                    ? <ToggleRight size={28} className="text-exito-400" />
                                                    : <ToggleLeft size={28} className="text-slate-400 dark:text-gray-600" />}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => openEdit(u)}
                                                    className="p-2 rounded-lg bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                                                    title="Editar"
                                                >
                                                    <Edit2 size={15} />
                                                </button>
                                                {!isMe(u) && !isAdmin(u) && (
                                                    <button
                                                        onClick={() => handleDelete(u)}
                                                        className="p-2 rounded-lg bg-error-500/10 hover:bg-error-500/30 text-error-400 transition-colors"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!loading && usuarios.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-16 text-center text-slate-400 dark:text-gray-500">
                                        <Users size={40} className="mx-auto mb-3 opacity-20" />
                                        <p>No hay usuarios creados todavía.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Crear / Editar */}
            {modal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-slate-200 dark:border-white/10 flex justify-between items-center bg-slate-50 dark:bg-gray-800/40">
                            <h3 className="titulo-modal">
                                <UserPlus className="text-brand-800" size={22} />
                                {modal === 'edit' ? 'Editar Usuario' : 'Nuevo Usuario'}
                            </h3>
                            <button onClick={closeModal} className="text-slate-500 dark:text-gray-500 hover:text-slate-900 dark:hover:text-white transition-colors">
                                <X size={22} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {error && (
                                <div className="bg-error-500/10 border border-error-500/20 text-error-400 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                                    <AlertCircle size={16} />
                                    {error}
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label htmlFor="usuario-nombre-de-usuario" className="etiqueta-campo">Nombre de usuario *</label>
                                    <input id="usuario-nombre-de-usuario" required type="text"
                                        className="campo"
                                        value={formData.username}
                                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="usuario-nombre" className="etiqueta-campo">Nombre</label>
                                    <input id="usuario-nombre" type="text"
                                        className="campo"
                                        value={formData.first_name}
                                        onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="usuario-apellido" className="etiqueta-campo">Apellido</label>
                                    <input id="usuario-apellido" type="text"
                                        className="campo"
                                        value={formData.last_name}
                                        onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="usuario-correo-electronico" className="etiqueta-campo">Correo electrónico *</label>
                                <input id="usuario-correo-electronico" required type="email"
                                    className="campo"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>

                            <div>
                                <label htmlFor="usuario-clave" className="etiqueta-campo">
                                    {modal === 'edit' ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}
                                </label>
                                <input id="usuario-clave"
                                    required={modal === 'create'}
                                    type="password"
                                    autoComplete="new-password"
                                    className="campo"
                                    value={formData.password}
                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                />
                            </div>

                            {/* Rol */}
                            <div>
                                <span id="usuario-rol-etiqueta" className="etiqueta-campo">Rol *</span>
                                {formData.is_superuser ? (
                                    <div className="px-4 py-2.5 rounded-xl bg-brand-500/10 border border-brand-500/20 text-brand-800 text-sm font-bold flex items-center gap-2">
                                        <ShieldCheck size={16} />
                                        Administrador (no se puede cambiar)
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-labelledby="usuario-rol-etiqueta">
                                        {ROLES.map(r => {
                                            const m = ROL_META[r];
                                            return (
                                                <button
                                                    key={r}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={formData.rol === r}
                                                    onClick={() => setFormData({ ...formData, rol: r })}
                                                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border font-bold text-sm transition-all ${
                                                        formData.rol === r
                                                            ? `${m.color} ring-2 ring-offset-1 ring-offset-white dark:ring-offset-gray-900 ring-current`
                                                            : 'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-white/10'
                                                    }`}
                                                >
                                                    {m.icon}
                                                    {r}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Estado (solo en edición, no en creación) */}
                            {modal === 'edit' && !isMe(formData) && (
                                <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-white/5 rounded-xl">
                                    <label htmlFor="usuario-activo" className="text-sm font-semibold text-slate-600 dark:text-gray-300 flex-1">Usuario activo</label>
                                    <button type="button" id="usuario-activo" role="switch" aria-checked={formData.is_active}
                                        onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${formData.is_active ? 'bg-exito-500' : 'bg-gray-600'}`}
                                    >
                                        <span className={`inline-block w-5 h-5 bg-white rounded-full transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                            )}

                            <div className="pie-modal">
                                <button type="button" onClick={closeModal}
                                    className="px-5 py-2.5 rounded-xl text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                                    Cancelar
                                </button>
                                <button type="submit" disabled={saving}
                                    className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-negro-950 px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-brand-500/20 transition-all">
                                    {saving ? 'Guardando...' : modal === 'edit' ? 'Guardar Cambios' : 'Crear Usuario'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
